import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Gift, Loader2, Save, RefreshCcw, Info, Sparkles, Clock, Calendar } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminHypeConnectPromos({ authToken }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [threeDay, setThreeDay] = useState(true);
  const [thirtyDay, setThirtyDay] = useState(true);
  const [customActive, setCustomActive] = useState(false);
  const [customText, setCustomText] = useState("");

  const fetchPromos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/hypeconnect-promotions`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const d = res.data || {};
      setThreeDay(d.three_day_active ?? true);
      setThirtyDay(d.thirty_day_active ?? true);
      setCustomActive(d.custom_active ?? false);
      setCustomText(d.custom_text || "");
    } catch (e) {
      toast.error("Aktionen konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    fetchPromos();
  }, [fetchPromos]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/hypeconnect-promotions`,
        {
          three_day_active: threeDay,
          thirty_day_active: thirtyDay,
          custom_active: customActive,
          custom_text: customText,
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      toast.success("Aktionen aktualisiert – Lisa antwortet ab jetzt entsprechend.");
    } catch (e) {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  const noneActive = !threeDay && !thirtyDay && !(customActive && customText.trim());

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto space-y-6"
      data-testid="hypeconnect-promos-page"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-indigo-100 flex items-center justify-center">
            <Gift className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">HypeConnect Aktionen</h1>
            <p className="text-sm text-gray-500">
              Schalte Gratis-Aktionen ein oder aus – Lisa passt ihre Antworten in Echtzeit an.
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchPromos}
          data-testid="promos-refresh-btn"
        >
          <RefreshCcw className="h-4 w-4 mr-2" /> Neu laden
        </Button>
      </div>

      {/* Info banner */}
      <div className="flex gap-3 rounded-xl border border-indigo-100 bg-indigo-50/60 p-4 text-sm text-indigo-900">
        <Info className="h-5 w-5 flex-shrink-0 mt-0.5" />
        <div>
          Diese Schalter steuern, welche Aktionen Lisa im Beratungschat aktiv ins Gespräch bringt.
          Änderungen wirken sofort für <strong>alle neuen Antworten</strong> – bestehende
          Gesprächsfäden werden auf den neuen Stand gebracht.
        </div>
      </div>

      {noneActive && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="promos-none-active-warning">
          <strong>Aktuell keine Aktion aktiv.</strong> Lisa verweist Kunden in dem Fall freundlich
          auf die Jobseite (https://hypeconnect.info) und bietet info@hypeconnect.info an.
        </div>
      )}

      {/* 3-Day toggle */}
      <PromoCard
        icon={<Clock className="h-5 w-5 text-blue-600" />}
        accent="from-blue-50 to-blue-50/30"
        title="3-Tage-Gratis-Aktion"
        subtitle={'Self-Service Test über den orangen „✨ Anzeige mit KI erstellen"-Button'}
        active={threeDay}
        onToggle={setThreeDay}
        testId="promo-three-day"
        details={[
          "Kein Code, kein Limit, läuft 3 Tage und endet automatisch",
          "Ideal für unsichere oder preissensible Kunden als sanfter Hebel",
          "Wird bei Preisbedenken proaktiv von Lisa angeboten",
        ]}
      />

      {/* 30-Day toggle */}
      <PromoCard
        icon={<Calendar className="h-5 w-5 text-emerald-600" />}
        accent="from-emerald-50 to-emerald-50/30"
        title="30-Tage-Sonderaktion (erste 100 Arbeitgeber)"
        subtitle="Limitiertes Testkontingent mit Gutscheincode per E-Mail"
        active={thirtyDay}
        onToggle={setThirtyDay}
        testId="promo-thirty-day"
        details={[
          "Per E-Mail an info@hypeconnect.info – Code wird zugeschickt",
          "30 Tage komplett gratis, keine Kreditkarte, kein Abo",
          "Für Kunden, die richtig testen und Bewerbungen sammeln wollen",
        ]}
      />

      {/* Custom promo */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm" data-testid="promo-custom-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-purple-100 to-purple-50 flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Custom-Aktion</h2>
              <p className="text-sm text-gray-500">
                Beschreibe eine eigene Aktion. Lisa baut sie sinnvoll in passende Antworten ein.
              </p>
            </div>
          </div>
          <Switch
            checked={customActive}
            onCheckedChange={setCustomActive}
            data-testid="promo-custom-toggle"
          />
        </div>

        <div className="mt-4 space-y-2">
          <label className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Aktions-Beschreibung
          </label>
          <Textarea
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            placeholder={'Beispiel: "Frühjahrs-Aktion: Anzeige läuft 14 Tage komplett gratis bis 30.04.2026. Kein Code nötig, kein Abo, automatisches Ende. Bei Interesse: info@hypeconnect.info"'}
            className="min-h-[120px] text-sm"
            disabled={!customActive}
            data-testid="promo-custom-text"
          />
          <p className="text-xs text-gray-400">
            Tipp: Schreib in 2-4 Sätzen, was die Aktion ist, wie lange sie läuft und wie der Kunde teilnehmen kann.
            Lisa formuliert das selbst aus – also keinen fertigen Werbe-Slogan, sondern die Fakten.
          </p>
        </div>
      </div>

      {/* Save */}
      <div className="sticky bottom-4 flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg"
          data-testid="promos-save-btn"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Speichern…
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" /> Aktionen speichern
            </>
          )}
        </Button>
      </div>
    </motion.div>
  );
}

function PromoCard({ icon, accent, title, subtitle, active, onToggle, details, testId }) {
  return (
    <div
      className={`rounded-2xl border border-gray-200 bg-gradient-to-br ${accent} bg-white p-5 shadow-sm transition-opacity ${active ? "" : "opacity-70"}`}
      data-testid={`${testId}-card`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3">
          <div className="h-10 w-10 rounded-xl bg-white/80 flex items-center justify-center flex-shrink-0">
            {icon}
          </div>
          <div>
            <h2 className="font-semibold text-gray-900">{title}</h2>
            <p className="text-sm text-gray-600">{subtitle}</p>
          </div>
        </div>
        <Switch checked={active} onCheckedChange={onToggle} data-testid={`${testId}-toggle`} />
      </div>
      <ul className="mt-3 ml-13 space-y-1 text-sm text-gray-600 pl-13">
        {details.map((d, i) => (
          <li key={i} className="flex gap-2">
            <span className="text-gray-400">•</span>
            <span>{d}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
